import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import {
  ChatAttachmentViewerComponent,
  chatAttachmentViewerConfig,
} from '@blaze-shared/shared-feature';
import { MarkdownPipe, TooltipDirective } from '@blaze-shared/shared-ui';
import { getItemTypeIcon, UploaderFacade } from '@blaze-shared/uploader';

import { ApplicationDialogService } from '../../../services/dialog/application-dialog.service';
import { FileService } from '../../../services/file.service';
import { ChatMemberInfo, ChatMessage } from '../chat.models';
import { InitialsPipe } from '../user-avatar/initials.pipe';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import { UserStatusDescriptionPipe } from '../utils/user-status-description.pipe';

type AttachmentExtensionMap = Record<string, { ext: string; filename: string }>;

@Component({
  selector: 'be-chat-message',
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
  imports: [
    MatDialogModule,
    InitialsPipe,
    UserAvatarComponent,
    NgOptimizedImage,
    TooltipDirective,
    MarkdownPipe,
    MatIconModule,
    UserStatusDescriptionPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageComponent {
  private readonly applicationDialogService = inject(ApplicationDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly fileService = inject(FileService);
  private readonly uploaderFacade = inject(UploaderFacade);

  readonly message = input.required<ChatMessage>();
  readonly userStatus = input<ChatMemberInfo | undefined>(undefined);
  readonly colorInfo = input<{ foreground: string; background: string } | undefined>(undefined);
  readonly hasFocus = input(false);
  readonly isSentByCurrentUser = input(false);
  readonly messageEditInProgress = input(false);
  readonly modifiedAgo = input('');
  readonly showActionsOnHover = input(false);
  readonly showAuthorsImage = input(true);
  readonly showSender = input(true);
  readonly showTimeAgo = input(true);
  readonly splitLayout = input(false);
  readonly time = input('');
  readonly timeAgo = input('');

  readonly editMessage = output<void>();

  readonly userStatusArray = computed<ChatMemberInfo[]>(() =>
    [this.userStatus()].filter((user): user is ChatMemberInfo => !!user?.status),
  );

  readonly attachmentExtensionMap = computed<AttachmentExtensionMap>(() =>
    this.buildAttachmentExtensionMap(this.message().attachments),
  );

  readonly fileAttachments = computed<string[]>(() => {
    const map = this.attachmentExtensionMap();
    return this.message().attachments?.filter((a) => map[a]) ?? [];
  });

  readonly imageAttachments = computed<string[]>(() => {
    const map = this.attachmentExtensionMap();
    return this.message().attachments?.filter((a) => !map[a]) ?? [];
  });

  openAttachmentDialog(attachment: string): void {
    const map = this.attachmentExtensionMap();
    if (!map[attachment]) {
      const images = this.imageAttachments();
      this.dialog.open(
        ChatAttachmentViewerComponent,
        chatAttachmentViewerConfig({
          attachments: images,
          startIndex: Math.max(images.indexOf(attachment), 0),
        }),
      );
      return;
    }

    const { filename } = map[attachment];
    this.applicationDialogService
      .openConfirmDialog('Download file', `Do you want to download "${filename}"?`, [
        { id: 'download', label: 'Download', action: 'continue' },
        { id: 'cancel', label: 'Cancel', action: 'cancel' },
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((action) => {
        if (action === 'continue') {
          this.fileService.downloadFile(attachment);
        }
      });
  }

  private buildAttachmentExtensionMap(attachments?: string[]): AttachmentExtensionMap {
    const imageExtensions = this.uploaderFacade.supportedImageTypes;
    return attachments
      ? Object.fromEntries(
          attachments
            .map((attachment) => {
              const urlExt = '.' + attachment.split('.').pop()?.toLowerCase();
              if (!urlExt || !imageExtensions.includes(urlExt)) {
                const filename = attachment.split('/').pop() || '';
                return [attachment, { ext: getItemTypeIcon(attachment), filename }];
              }
              return [];
            })
            .filter(Boolean),
        )
      : {};
  }
}
